'use client';

import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { useMemo, type ReactNode } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import type { Folder } from '@/lib/bookmark/types';
import { DragHandle } from './drag-handle';
import { HandleWorkflowDropdown } from './handle-workflow-dropdown';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/16/solid';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { useWorkflows } from '@/providers/workflow-provider';
import { cn } from '@/lib/cn';

interface WorkflowAccordionProps {
  readonly workEngagementId: string | null;
  readonly isEditMode: boolean;
  readonly folder: Folder;
  readonly isDashboard: boolean;
  readonly dragHandleProps: DraggableProvidedDragHandleProps | null;
  readonly children: ReactNode;
}

export function WorkflowAccordion({
  workEngagementId,
  isEditMode,
  folder,
  isDashboard,
  dragHandleProps,
  children,
}: WorkflowAccordionProps) {
  const { accordionType, workflowSettings, toggleAccordion, workEngagements } =
    useWorkflows();

  const currentOpenAccordions =
    workflowSettings.expandedItems[accordionType] ?? [];
  const accordionValue = folder.client_id;

  const workEngagementNameById = useMemo(() => {
    const map = new Map<string, string>();
    workEngagements?.list?.forEach((w) => {
      const id = w.workEngagement?.id;
      if (id) map.set(id, w.workEngagement?.name ?? '');
    });
    return map;
  }, [workEngagements]);

  const role = useMemo(() => {
    if (!workEngagementId) return 'privat';
    return workEngagementNameById.get(workEngagementId) ?? 'arbetsroll';
  }, [workEngagementId, workEngagementNameById]);

  return (
    <Accordion
      type='multiple'
      value={
        currentOpenAccordions.includes(accordionValue) ? [accordionValue] : []
      }
      onValueChange={(values) => {
        toggleAccordion(accordionValue, values.includes(accordionValue));
      }}
      className={cn(
        'mb-3 flex flex-row overflow-hidden rounded-lg border bg-subtle/50 dark:bg-muted',
        folder.kind !== 'workflow' && 'hidden'
      )}
    >
      <DragHandle dragHandleProps={dragHandleProps} />
      <AccordionItem
        className='w-full overflow-hidden border-none'
        value={accordionValue}
      >
        <div className='flex items-center justify-between'>
          <AccordionTrigger className={`p-4 text-sm hover:no-underline`}>
            <div className='flex items-center gap-2 text-left'>
              {!isDashboard && (
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <span className='flex items-center'>
                      {folder.pinned ? (
                        <EyeIcon className='w-4' />
                      ) : (
                        <EyeSlashIcon className='w-4' />
                      )}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {folder.pinned
                      ? 'Visas på "Min arbetsdag"'
                      : 'Dold från "Min arbetsdag"'}
                  </TooltipContent>
                </Tooltip>
              )}
              <div className='flex items-center gap-1'>
                {folder.name}
                {isDashboard && (
                  <span className='flex items-center text-xs font-normal text-muted-foreground'>
                    {`(${role.toLowerCase()})`}
                  </span>
                )}
              </div>
            </div>
          </AccordionTrigger>
          <HandleWorkflowDropdown
            workEngagementId={workEngagementId}
            type='folder'
            currentItem={folder}
            isEditMode={isEditMode}
          />
        </div>
        <AccordionContent className='p-4 pt-0 [&[data-state=closed]]:animate-none [&[data-state=closed]]:transition-none [&[data-state=open]]:animate-none [&[data-state=open]]:transition-none'>
          {children}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
